import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  createDataTableColumnHelper,
  DataTable,
  DateRangePicker,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Field,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TableCellStack,
  Link as UiLink,
} from "@beweegbaas/ui";
import { parseDate, type DateValue } from "@internationalized/date";
import { createFileRoute, Link, Outlet, stripSearchParams } from "@tanstack/react-router";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { InsightsSubnavigation } from "#/components/insights-subnavigation";
import { Page } from "#/components/page";
import { PendingContent } from "#/components/pending-content";
import { getDefaultDateRange } from "#/modules/lesadministratie.statistics/filters.lesadministratie-statistieken";
import { getLessonAdministrationData } from "#/modules/lesadministratie.statistics/lesson-administration.functions";
import {
  LESSON_ADMINISTRATION_SEARCH_DEFAULTS,
  lessonAdministrationSearchSchema,
  type LesadministratieStatistiekenFilters,
} from "#/modules/lesadministratie.statistics/schema.search";
import { buildExportHref } from "#/modules/util.files/export-search-params";
import { renderMultiSelectValue } from "#/modules/util.helpers/utils.select";
import { m } from "#/paraglide/messages";
import type { LesadministratieStatistiekenLeerling } from "#/schema.nswag";
import IconMaterialSymbolsDownload from "~icons/material-symbols/download";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/insights/lesson-administration",
)({
  validateSearch: lessonAdministrationSearchSchema,
  // Keeps the default (empty) filter out of the URL.
  search: { middlewares: [stripSearchParams(LESSON_ADMINISTRATION_SEARCH_DEFAULTS)] },
  loaderDeps: ({ search }) => ({ klasIds: search.klasIds, from: search.from, to: search.to }),
  loader: ({ deps }) => getLessonAdministrationData({ data: { filters: deps } }),
  component: LessonAdministrationPage,
  errorComponent: LessonAdministrationError,
});

function TimeoutsTotaalCell({ row }: { row: LesadministratieStatistiekenLeerling }) {
  if (row.timeoutsTotaal === 0) {
    return <span>0</span>;
  }
  return (
    <UiLink
      variant="secondary"
      underline
      to="/$locale/{-$schoolyear}/$school/insights/lesson-administration/timeouts/$leerlingId"
      params={(prev: Record<string, string>) => ({ ...prev, leerlingId: row.leerlingId })}
      search={(prev: LesadministratieStatistiekenFilters) => prev}
      preventScrollReset
      aria-label={m["common.aria.open_list"]({ title: row.naam })}
    >
      {row.timeoutsTotaal}
    </UiLink>
  );
}

const helper = createDataTableColumnHelper<LesadministratieStatistiekenLeerling>();

const COUNT_META = {
  headerClassName: "min-w-[8ch] tabular-nums",
  cellClassName: "text-muted-foreground text-sm tabular-nums",
};

const columns = helper.columns([
  helper.accessor("naam", {
    header: () => m["common.fields.student"](),
    meta: { wrap: true, cellClassName: "font-medium" },
    cell: ({ row, getValue }) => (
      <TableCellStack secondary={row.original.klasNaam}>{getValue()}</TableCellStack>
    ),
  }),
  helper.accessor("afwezigheid", {
    header: () => m["lesadministratie.col_absence"](),
    meta: COUNT_META,
  }),
  helper.accessor("blessures", {
    header: () => m["lesadministratie.col_injury"](),
    meta: COUNT_META,
  }),
  helper.accessor("gymkleding", {
    header: () => m["common.labels.gymkleding"](),
    meta: COUNT_META,
  }),
  helper.accessor("korteTimeout", {
    header: () => m["common.labels.korte_timeout"](),
    meta: COUNT_META,
  }),
  helper.accessor("langeTimeout", {
    header: () => m["common.labels.lange_timeout"](),
    meta: COUNT_META,
  }),
  helper.accessor("uitsluitingLes", {
    header: () => m["common.labels.uitsluiting_les"](),
    meta: COUNT_META,
  }),
  helper.accessor("timeoutsTotaal", {
    header: () => m["lesadministratie.col_timeouts_totaal"](),
    meta: COUNT_META,
    cell: ({ row }) => <TimeoutsTotaalCell row={row.original} />,
  }),
]);

function LessonAdministrationPage() {
  const { locale, schoolyear, school } = Route.useParams();
  const { klassen, stats } = Route.useLoaderData();
  const filters = Route.useSearch();
  const navigate = Route.useNavigate();
  const setFilters = (patch: Partial<typeof filters>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true, resetScroll: false });
  const defaultRange = getDefaultDateRange();

  const hasNoClasses = klassen.length === 0;
  const klasSelectItems = klassen.map((k) => ({ value: k.id, label: k.name }));

  const fromStr = filters.from ?? defaultRange.from;
  const toStr = filters.to ?? defaultRange.to;

  let fromDateValue: DateValue;
  try {
    fromDateValue = parseDate(fromStr);
  } catch {
    fromDateValue = parseDate(defaultRange.from);
  }

  let toDateValue: DateValue;
  try {
    toDateValue = parseDate(toStr);
  } catch {
    toDateValue = parseDate(defaultRange.to);
  }

  const exportHref = buildExportHref(
    "lesson-administration",
    { locale, schoolyear, school },
    { klasIds: filters.klasIds, from: fromStr, to: toStr },
  );

  const data = stats;

  return (
    <Page>
      {!hasNoClasses && (
        <Page.Header>
          <Field className="w-full md:w-xs">
            <Select
              multiple
              value={filters.klasIds}
              onValueChange={(value) => {
                void setFilters({ klasIds: value ?? [] });
              }}
            >
              <SelectTrigger
                id="filter-classes"
                className="w-full"
                aria-label={m["common.fields.classes"]()}
              >
                <SelectValue>
                  {(value: string[] | null) =>
                    renderMultiSelectValue(
                      value,
                      klasSelectItems,
                      m["common.placeholders.all_classes"](),
                    )
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {klasSelectItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </Field>
          <DateRangePicker
            aria-label={m["common.labels.periode"]()}
            value={{ start: fromDateValue, end: toDateValue }}
            onChange={(range) => {
              if (range?.start && range?.end) {
                void setFilters({ from: range.start.toString(), to: range.end.toString() });
              }
            }}
            fieldGroupClassName="w-full min-w-0 sm:w-auto sm:min-w-[17rem]"
            className="w-full sm:w-auto"
          />
          <Page.Header.Actions>
            <Button
              variant="outline"
              render={<a href={exportHref} download />}
              nativeButton={false}
            >
              <IconMaterialSymbolsDownload className="shrink-0" />
              {m["common.actions.export_excel"]()}
            </Button>
          </Page.Header.Actions>
        </Page.Header>
      )}
      {hasNoClasses && <Page.MobileHeader />}
      <InsightsSubnavigation />
      {hasNoClasses ? (
        <div className="flex grow">
          <Empty>
            <EmptyHeader>
              <EmptyTitle>{m["common.empty_state.no_classes"]()}</EmptyTitle>
              <EmptyDescription>
                Importeer eerst leerlingen om lesadministratie per klas te bekijken.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button
                size="xl"
                render={
                  <Link
                    from={Route.fullPath}
                    to="/$locale/{-$schoolyear}/$school/detail/import/$type"
                    params={(prev: Record<string, string>) => ({
                      ...prev,
                      type: "modify-existing-year",
                    })}
                  />
                }
                nativeButton={false}
              >
                {m["common.empty_state.import_students"]()}
                <IconMaterialSymbolsArrowRightAltRounded />
              </Button>
            </EmptyContent>
          </Empty>
        </div>
      ) : (
        <Page.Body className="min-h-0">
          {data == null ? null : (
            <PendingContent className="flex flex-col gap-8">
              <Page.Container className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/$school/insights/lesson-administration/stats/$kpi"
                  params={(prev: Record<string, string>) => ({ ...prev, kpi: "afwezigheden" })}
                  search={(prev) => prev}
                  resetScroll={false}
                  aria-label={m["common.aria.open_list"]({
                    title: m["lesadministratie.kpi_afwezigheden"](),
                  })}
                >
                  <Card size="sm">
                    <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                      <CardTitle>{m["lesadministratie.kpi_afwezigheden"]()}</CardTitle>
                      <IconTablerUserX
                        className="text-muted-foreground size-6 shrink-0"
                        aria-hidden
                      />
                    </CardHeader>
                    <CardContent className="space-y-1">
                      <p className="text-2xl font-semibold tabular-nums">
                        {data.afwezigheden.count}
                      </p>
                      <p className="text-muted-foreground text-sm">
                        {m["lesadministratie.kpi_subtitle"]({
                          total: data.afwezigheden.total,
                        })}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/$school/insights/lesson-administration/stats/$kpi"
                  params={(prev: Record<string, string>) => ({ ...prev, kpi: "blessures" })}
                  search={(prev) => prev}
                  resetScroll={false}
                  aria-label={m["common.aria.open_list"]({
                    title: m["lesadministratie.col_injury"](),
                  })}
                >
                  <Card size="sm">
                    <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                      <CardTitle>{m["lesadministratie.col_injury"]()}</CardTitle>
                      <IconTablerBandage
                        className="text-muted-foreground size-6 shrink-0"
                        aria-hidden
                      />
                    </CardHeader>
                    <CardContent className="space-y-1">
                      <p className="text-2xl font-semibold tabular-nums">{data.blessures.count}</p>
                      <p className="text-muted-foreground text-sm">
                        {m["lesadministratie.kpi_subtitle"]({ total: data.blessures.total })}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/$school/insights/lesson-administration/stats/$kpi"
                  params={(prev: Record<string, string>) => ({
                    ...prev,
                    kpi: "gymkleding-vergeten",
                  })}
                  search={(prev) => prev}
                  resetScroll={false}
                  aria-label={m["common.aria.open_list"]({
                    title: m["lesadministratie.kpi_gymkleding"](),
                  })}
                >
                  <Card size="sm">
                    <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                      <CardTitle>{m["lesadministratie.kpi_gymkleding"]()}</CardTitle>
                      <IconTablerShirt
                        className="text-muted-foreground size-6 shrink-0"
                        aria-hidden
                      />
                    </CardHeader>
                    <CardContent className="space-y-1">
                      <p className="text-2xl font-semibold tabular-nums">
                        {data.gymkledingVergeten.count}
                      </p>
                      <p className="text-muted-foreground text-sm">
                        {m["lesadministratie.kpi_subtitle"]({
                          total: data.gymkledingVergeten.total,
                        })}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/$school/insights/lesson-administration/stats/$kpi"
                  params={(prev: Record<string, string>) => ({
                    ...prev,
                    kpi: "disciplinaire-maatregel",
                  })}
                  search={(prev) => prev}
                  resetScroll={false}
                  aria-label={m["common.aria.open_list"]({
                    title: m["lesadministratie.kpi_discipline"](),
                  })}
                >
                  <Card size="sm">
                    <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                      <CardTitle>{m["lesadministratie.kpi_discipline"]()}</CardTitle>
                      <IconTablerGavel
                        className="text-muted-foreground size-6 shrink-0"
                        aria-hidden
                      />
                    </CardHeader>
                    <CardContent className="space-y-1">
                      <p className="text-2xl font-semibold tabular-nums">
                        {data.disciplinaireMaatregel.count}
                      </p>
                      <p className="text-muted-foreground text-sm">
                        {m["lesadministratie.kpi_subtitle"]({
                          total: data.disciplinaireMaatregel.total,
                        })}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              </Page.Container>

              <DataTable
                alignToContainer
                size="small"
                aria-label={m["lesadministratie.table_aria"]()}
                columns={columns}
                data={data.leerlingen}
                getRowId={(row) => row.leerlingId}
              />
            </PendingContent>
          )}
        </Page.Body>
      )}
      <Outlet />
    </Page>
  );
}

function LessonAdministrationError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}
